import { useMemo } from 'react';

import type { ObjectiveWindow } from '@/types/objectives';

import { ALL_PERIODS_OPTION_ID } from '@/constants/ObjectivePeriodLens';
import { useEmployeeObjectives } from '@/hooks/objectives/useEmployeeObjectives';
import { usePeriodOptions } from '@/hooks/objectives/usePeriodOptions';

/**
 * The employee's objectives read through the profile's period lens: the periods on offer, the one
 * in effect, the objectives the server returns for it and their headline figures. Shared by the
 * Objectives tab and the assessment tab's objectives card, so both count the same objectives.
 *
 * `pickedPeriodId` is the reader's choice, owned by the page; `null` falls back to the default
 * period. No period at all leaves `selectedPeriod` undefined and the server returns every objective.
 */
interface EmployeeObjectivesInPeriodParams {
    employeeId: string;
    pickedPeriodId: string | null;
}

export const useEmployeeObjectivesInPeriod = ({
    employeeId,
    pickedPeriodId,
}: EmployeeObjectivesInPeriodParams) => {
    const {
        options: periodOptions,
        defaultPeriodId,
        isLoading: isLoadingPeriods,
        isError: arePeriodsInError,
    } = usePeriodOptions();
    const requestedPeriodId = pickedPeriodId ?? defaultPeriodId;
    const selectedPeriod = periodOptions.find((option) => option.id === requestedPeriodId);
    // The lens itself, which the picker shows and which names the window: a cycle, or "all
    // periods", which is no cycle and would otherwise leave the control on its placeholder with
    // nothing on screen saying what the figures below cover.
    const periodId =
        selectedPeriod?.id ??
        (requestedPeriodId === ALL_PERIODS_OPTION_ID ? ALL_PERIODS_OPTION_ID : '');

    const arePeriodsKnown = !isLoadingPeriods && !arePeriodsInError;

    const objectiveWindow = useMemo<ObjectiveWindow>(
        () => ({ employeeId, objectivePeriodId: selectedPeriod?.id }),
        [employeeId, selectedPeriod?.id],
    );
    // Asked before the periods land, the window has no period yet: the server would answer for
    // every period, then again for the default one, and the list would switch under the reader.
    const { objectives, isLoadingObjectives, areObjectivesInError, areObjectivesKnown, summary } =
        useEmployeeObjectives(objectiveWindow, arePeriodsKnown);

    return {
        periodOptions,
        periodId,
        selectedPeriod,
        isLoadingPeriods,
        objectives,
        // A request held back for its period is still on its way, not an empty answer.
        isLoadingObjectives: isLoadingPeriods || isLoadingObjectives,
        summary,
        // Either request failing leaves the figures unknown, not empty: failed periods would read
        // as "across all periods", failed objectives as "no objective".
        isError: arePeriodsInError || areObjectivesInError,
        // `summarizeObjectives([])` answers zero for everything, and the list is empty before either
        // request lands. Shown as figures, those zeros would read as a statement about this
        // employee, so a consumer withholds its figures until both requests have succeeded.
        isSummaryKnown: arePeriodsKnown && areObjectivesKnown,
    };
};
